import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { DataGridPro, gridClasses, GridLogicOperator } from '@mui/x-data-grid-pro'
import CustomToolbar from 'components/configured-service/CreateDraftConfiguredServiceDialog/CustomToolbar'
import { IconButton, LinearProgress, ListItemIcon, ListItemText, Menu, MenuItem } from '@mui/material'
import ImportExportIcon from '@mui/icons-material/ImportExport'
import CheckIcon from '@mui/icons-material/Check'
import { useGetColumns } from 'components/configured-service/CreateDraftConfiguredServiceDialog/settings'
import {
  selectCSDFilterModel,
  selectCSDRowSelectionModel,
  setFilterModel,
  setRowSelectionModel,
} from 'slices/configuredService/createDraftCSDSlice'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { useFormContext } from 'react-hook-form'
import { useLazyGetAllPricedServicesQuery } from 'api/pricing/pricingServiceCrud'
import sortBy from 'lodash/sortBy'
import { Z_INDEX } from 'theme/zIndex'
import { LOCAL_STORAGE_KEYS } from 'settings/constants/localStorage'

const SORT_OPTIONS = {
  ALPHABETICAL: 'ALPHABETICAL',
  RECENTLY_ADDED: 'RECENTLY_ADDED',
  MOST_FREQUENTLY_USED: 'MOST_FREQUENTLY_USED',
}

const CreateDraftServiceTemplateList = () => {
  const rowSelectionModel = useSelector(selectCSDRowSelectionModel, shallowEqual)
  const filterModel = useSelector(selectCSDFilterModel, shallowEqual)
  const [searchPricingService, { data, isLoading }] = useLazyGetAllPricedServicesQuery()
  const { setValue, resetField, watch } = useFormContext()
  const quantity = watch('quantity')
  const dispatch = useDispatch()

  const [sortOption, setSortOption] = useState(() => localStorage.getItem(LOCAL_STORAGE_KEYS.CSD_SORT_OPTION) || SORT_OPTIONS.ALPHABETICAL)
  const [sortMenuAnchor, setSortMenuAnchor] = useState(null)

  const handleSortMenuOpen = useCallback(e => setSortMenuAnchor(e.currentTarget), [])
  const handleSortMenuClose = useCallback(() => setSortMenuAnchor(null), [])
  const handleSortSelect = useCallback(
    option => {
      setSortOption(option)
      localStorage.setItem(LOCAL_STORAGE_KEYS.CSD_SORT_OPTION, option)
      handleSortMenuClose()
    },
    [handleSortMenuClose]
  )

  const sortButton = (
    <>
      <IconButton size="small" onClick={handleSortMenuOpen} color="primary">
        <ImportExportIcon fontSize="small" />
      </IconButton>
      <Menu anchorEl={sortMenuAnchor} open={Boolean(sortMenuAnchor)} onClose={handleSortMenuClose} sx={{ zIndex: Z_INDEX.MENU }}>
        <MenuItem onClick={() => handleSortSelect(SORT_OPTIONS.ALPHABETICAL)}>
          <ListItemIcon>{sortOption === SORT_OPTIONS.ALPHABETICAL ? <CheckIcon fontSize="small" /> : null}</ListItemIcon>
          <ListItemText>Alphabetical (A–Z)</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleSortSelect(SORT_OPTIONS.RECENTLY_ADDED)}>
          <ListItemIcon>{sortOption === SORT_OPTIONS.RECENTLY_ADDED ? <CheckIcon fontSize="small" /> : null}</ListItemIcon>
          <ListItemText>Recently Added</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleSortSelect(SORT_OPTIONS.MOST_FREQUENTLY_USED)}>
          <ListItemIcon>{sortOption === SORT_OPTIONS.MOST_FREQUENTLY_USED ? <CheckIcon fontSize="small" /> : null}</ListItemIcon>
          <ListItemText>Most Frequently Used</ListItemText>
        </MenuItem>
      </Menu>
    </>
  )

  const filterColumns = ({ field, columns: availableColumns, currentFilters }) => {
    const filteredFields = currentFilters?.map(item => item.field)
    return availableColumns
      .filter(colDef => colDef.filterable && (colDef.field === field || !filteredFields.includes(colDef.field)))
      .map(column => column.field)
  }

  const getColumnForNewFilter = ({ currentFilters, columns: availableColumns }) => {
    const filteredFields = currentFilters?.map(({ field }) => field)
    const columnForNewFilter = availableColumns
      .filter(colDef => colDef.filterable && !filteredFields.includes(colDef.field))
      .find(colDef => colDef.filterOperators?.length)
    return columnForNewFilter?.field ?? null
  }

  const getRowHeight = useCallback(() => 68, [])

  const getRowSpacing = useCallback(params => {
    return {
      top: 5,
      bottom: params.isLastVisible ? 0 : 5,
    }
  }, [])

  const onFilterChange = useCallback(newFilterModel => {
    dispatch(setFilterModel(newFilterModel))
  }, [])

  const columns = useGetColumns()
  const columnVisibilityModel = useMemo(
    () => ({
      serviceName: false,
      serviceType: false,
      businessLine: false,
      materialType: false,
      measureText: false,
      methodType: false,
      actionType: false,
      zoneName: false,
      recurrenceFrequency: false,
      pricingPeriod: false,
      recurrenceInterval: false,
    }),
    [columns]
  )

  const pricingServices = useMemo(() => {
    if (!Array.isArray(data)) return []
    const activeServices = data.filter(({ active = false }) => active === true)
    if (sortOption === SORT_OPTIONS.RECENTLY_ADDED) {
      return sortBy(activeServices, item => -(item?.createdAt ? new Date(item.createdAt).getTime() : 0))
    }
    if (sortOption === SORT_OPTIONS.MOST_FREQUENTLY_USED) {
      return sortBy(activeServices, item => -(item?.configuredServiceCount || 0))
    }
    return sortBy(activeServices, item => (item?.serviceName || '').toString().toLowerCase())
  }, [data, sortOption])

  const handleRowSelectionModelChange = useCallback(
    newRowSelectionModel => {
      dispatch(setRowSelectionModel(newRowSelectionModel))
      if (newRowSelectionModel.length > 0) {
        setValue('pricedServiceId', newRowSelectionModel[0], { shouldValidate: true })
        const selectedService = pricingServices.find(service => service.id === newRowSelectionModel[0])
        if (selectedService) {
          setValue('selectedPricedService', selectedService)
          const newPrice = selectedService.value * quantity
          resetField('price', { defaultValue: newPrice })
          setValue('price', newPrice, { shouldValidate: true })
        }
      } else {
        // Keep user's price intact when selection is cleared (e.g., during tab switch or explicit deselection)
        setValue('pricedServiceId', '', { shouldValidate: true })
      }
    },
    [dispatch, setValue, resetField, pricingServices, quantity]
  )

  useEffect(() => {
    searchPricingService().unwrap()
  }, [])

  return (
    <DataGridPro
      columns={columns}
      sx={{
        height: '100%',
        width: '100%',
        '&': {
          border: 0,
        },
        [`& .${gridClasses.cell}`]: {
          borderRadius: 2,
          border: '1px solid #E0E0E0',
        },
        '& .MuiDataGrid-main': {
          overflow: 'auto',
        },
        '& .MuiDataGrid-virtualScroller': {
          overflow: 'auto',
        },
        '& .MuiDataGrid-virtualScrollerContent': {
          minWidth: 'auto',
          width: '100%',
        },
        '& .MuiDataGrid-scrollbar': {
          pl: 0.1,
        },
      }}
      slotProps={{
        filterPanel: {
          logicOperators: [GridLogicOperator.And],
          filterFormProps: {
            filterColumns,
          },
          getColumnForNewFilter,
        },
        toolbar: {
          showQuickFilter: true,
          sortButton,
        },
      }}
      slots={{
        loadingOverlay: LinearProgress,
        toolbar: CustomToolbar,
      }}
      disableColumnMenu
      onRowSelectionModelChange={handleRowSelectionModelChange}
      rowSelectionModel={rowSelectionModel}
      disableMultipleRowSelection
      columnHeaderHeight={0}
      filterModel={filterModel}
      disableDensitySelector
      disableColumnSelector
      columnVisibilityModel={columnVisibilityModel}
      showToolbar
      hideFooter
      showCellVerticalBorder={false}
      showColumnVerticalBorder={false}
      getRowSpacing={getRowSpacing}
      getRowHeight={getRowHeight}
      rows={pricingServices}
      loading={isLoading}
      onFilterModelChange={onFilterChange}
    />
  )
}

export default CreateDraftServiceTemplateList
